import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { ArrowBackIcon, AddIcon, DeleteIcon, EditIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import AddDayOffModalChakra from "../components/days-off/AddDayOffModalChakra";
import OnboardingLayout from "../components/onboarding/OnboardingLayout";

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const CATEGORIES = {
  holiday: { label: 'Holiday', color: 'bg-red-100 text-red-800 border-red-200' },
  vacation: { label: 'Vacation', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  personal: { label: 'Personal Day', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  sick: { label: 'Sick Day', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-800 border-gray-200' }
};

export default function OnboardingAvailability() {
  const navigate = useNavigate();
  const toast = useToast();
  const timezoneDropdownRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Timezone state
  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [currentTimezone, setCurrentTimezone] = useState("");
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);

  // Workers state
  const [workers, setWorkers] = useState([]);
  const [workersAvailability, setWorkersAvailability] = useState({});
  const [expandedWorker, setExpandedWorker] = useState(null);

  // Days off state
  const [allDaysOff, setAllDaysOff] = useState([]);
  const [showAddDayOffModal, setShowAddDayOffModal] = useState(false);

  // Editing state
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [newSlot, setNewSlot] = useState({ day: null, startTime: '09:00', endTime: '17:00' });
  const [editingSlot, setEditingSlot] = useState(null);
  const [editSlotData, setEditSlotData] = useState({ startTime: '', endTime: '' });

  // Worker search
  const [workerSearch, setWorkerSearch] = useState("");

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timezoneDropdownRef.current && !timezoneDropdownRef.current.contains(event.target)) {
        setShowTimezoneDropdown(false);
        setTimezoneSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      // Load timezone, workers, and days off in parallel
      const [userResponse, timezonesResponse, workersResponse] = await Promise.all([
        fetch("/api/current_user", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
        fetch("/api/timezones", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
        fetch("/api/schedule_openings/filter_options", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
      ]);

      const userData = await userResponse.json();
      const timezonesData = await timezonesResponse.json();
      const workersData = await workersResponse.json();

      if (userData.user) {
        setCurrentTimezone(userData.user.timezone);
        setSelectedTimezone(userData.user.timezone);
      }

      if (timezonesData.success) {
        setTimezones(timezonesData.data);
      }

      if (workersData.workers) {
        setWorkers(workersData.workers);

        // Load availability for all workers
        await loadAllWorkersAvailability(workersData.workers, csrfToken);

        // Load days off for all workers
        await loadAllDaysOff(workersData.workers, csrfToken);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast({
        title: "Error loading data",
        description: "Failed to load initial data. Please refresh the page.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllWorkersAvailability = async (workersList, csrfToken) => {
    const availabilityPromises = workersList.map(worker =>
      fetch(`/api/workers/${worker.id}/availabilities`, {
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      }).then(res => res.json())
    );

    const results = await Promise.all(availabilityPromises);

    const availabilityMap = {};
    results.forEach((result, index) => {
      availabilityMap[workersList[index].id] = result.availabilities || {};
    });

    setWorkersAvailability(availabilityMap);
  };

  const loadAllDaysOff = async (workersList, csrfToken) => {
    const daysOffPromises = workersList.map(worker =>
      fetch(`/api/workers/${worker.id}/days_off`, {
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      }).then(res => res.json())
    );

    const results = await Promise.all(daysOffPromises);

    const allDays = results.flatMap((result, index) =>
      result.days_off.map(dayOff => ({
        ...dayOff,
        worker_name: workersList[index].name
      }))
    );

    setAllDaysOff(allDays);
  };

  const handleTimezoneChange = async () => {
    if (selectedTimezone === currentTimezone) {
      return;
    }

    try {
      setSaving(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch("/api/current_user/update_timezone", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ user: { timezone: selectedTimezone } }),
      });

      const data = await response.json();

      if (data.success) {
        setCurrentTimezone(selectedTimezone);
        toast({
          title: "Timezone updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join(", ") || "Failed to update timezone",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating timezone:", error);
      toast({
        title: "Error",
        description: "Failed to update timezone",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAvailable = async (workerId, currentAvailable) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/workers/${workerId}/toggle_availability`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ available: !currentAvailable }),
      });

      const data = await response.json();

      if (data.success) {
        setWorkers(workers.map(w =>
          w.id === workerId ? { ...w, available: data.worker.available } : w
        ));

        toast({
          title: `Worker ${data.worker.available ? 'enabled' : 'disabled'}`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update worker availability",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error toggling availability:", error);
      toast({
        title: "Error",
        description: "Failed to update worker availability",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddTimeSlot = async (workerId, dayOfWeek) => {
    try {
      setSaving(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/workers/${workerId}/availabilities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          availability: {
            day_of_week: dayOfWeek,
            start_time: newSlot.startTime,
            end_time: newSlot.endTime,
            active: true,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reload availability for this worker
        const availResponse = await fetch(`/api/workers/${workerId}/availabilities`, {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        });
        const availData = await availResponse.json();

        setWorkersAvailability({
          ...workersAvailability,
          [workerId]: availData.availabilities || {}
        });

        setNewSlot({ day: null, startTime: '09:00', endTime: '17:00' });
        setEditingAvailability(null);

        toast({
          title: "Time slot added",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join(", ") || "Failed to add time slot",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error adding time slot:", error);
      toast({
        title: "Error",
        description: "Failed to add time slot",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTimeSlot = async (workerId, slotId) => {
    try {
      setSaving(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/availabilities/${slotId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          availability: {
            start_time: editSlotData.startTime,
            end_time: editSlotData.endTime,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reload availability for this worker
        const availResponse = await fetch(`/api/workers/${workerId}/availabilities`, {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        });
        const availData = await availResponse.json();

        setWorkersAvailability({
          ...workersAvailability,
          [workerId]: availData.availabilities || {}
        });

        setEditingSlot(null);
        setEditSlotData({ startTime: '', endTime: '' });

        toast({
          title: "Time slot updated",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join(", ") || "Failed to update time slot",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating time slot:", error);
      toast({
        title: "Error",
        description: "Failed to update time slot",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTimeSlot = async (workerId, slotId) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/availabilities/${slotId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const data = await response.json();

      if (data.success) {
        // Reload availability for this worker
        const availResponse = await fetch(`/api/workers/${workerId}/availabilities`, {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        });
        const availData = await availResponse.json();

        setWorkersAvailability({
          ...workersAvailability,
          [workerId]: availData.availabilities || {}
        });

        toast({
          title: "Time slot deleted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting time slot:", error);
      toast({
        title: "Error",
        description: "Failed to delete time slot",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddDayOff = async (formData) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const baseUserId = formData.applyToUserIds[0];

      const response = await fetch(`/api/workers/${baseUserId}/days_off`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          user_ids: formData.applyToUserIds,
          day_off: {
            name: formData.name,
            category: formData.category,
            start_date: formData.startDate,
            end_date: formData.endDate,
            start_time: formData.isPartialDay ? formData.startTime : null,
            end_time: formData.isPartialDay ? formData.endTime : null,
            notes: formData.notes,
            active: true
          }
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowAddDayOffModal(false);
        await loadAllDaysOff(workers, csrfToken);

        toast({
          title: "Day off added",
          description: data.count > 1 ? `Added for ${data.count} workers` : undefined,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join('\n') || 'Failed to add day off',
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to add day off:', err);
      toast({
        title: "Error",
        description: "Failed to add day off",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteDayOff = async (id) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/days_off/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      const data = await response.json();

      if (data.success) {
        await loadAllDaysOff(workers, csrfToken);
        toast({
          title: "Day off deleted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to delete day off:', err);
      toast({
        title: "Error",
        description: "Failed to delete day off",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const convertTo12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatTimeSlots = (slots) => {
    if (!slots || slots.length === 0) return "Off";
    return slots.map(s => `${convertTo12Hour(s.start_time)}-${convertTo12Hour(s.end_time)}`).join(", ");
  };

  const filteredTimezones = timezones.filter(tz =>
    tz.label.toLowerCase().includes(timezoneSearch.toLowerCase()) ||
    tz.name.toLowerCase().includes(timezoneSearch.toLowerCase())
  );

  const getSelectedTimezoneLabel = () => {
    const tz = timezones.find(t => t.name === selectedTimezone);
    return tz ? tz.label : selectedTimezone;
  };

  const filteredWorkers = workers.filter(worker =>
    worker.name.toLowerCase().includes(workerSearch.toLowerCase()) ||
    worker.first_name?.toLowerCase().includes(workerSearch.toLowerCase()) ||
    worker.last_name?.toLowerCase().includes(workerSearch.toLowerCase())
  );

  const handleSkip = () => {
    navigate("/");
  };

  const handleContinue = () => {
    navigate("/onboarding/turn-on");
  };

  if (loading) {
    return (
      <OnboardingLayout title="Set Your Workers' Availability">
        <Flex minH="300px" align="center" justify="center">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Flex>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      title="Set Your Workers' Availability"
      subtitle="Configure when your team is available for appointments. Default hours are Monday-Friday, 8am-6pm."
    >
      <HStack mb={6} justify="space-between">
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          size="sm"
          color="gray.600"
          onClick={() => navigate("/onboarding/waitlist/customize")}
          _hover={{ bg: "gray.100" }}
        >
          Back
        </Button>
        <Button
          variant="ghost"
          size="sm"
          color="gray.600"
          onClick={handleSkip}
          _hover={{ bg: "gray.100" }}
        >
          Skip
        </Button>
      </HStack>

      {/* Timezone Section */}
            <Box
              mt={8}
              p={6}
              borderRadius="xl"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
            >
              <Heading size="sm" mb={4}>
                Timezone
              </Heading>
              <HStack spacing={3} align="start">
                <FormControl flex={1} position="relative" ref={timezoneDropdownRef}>
                  <Input
                    value={showTimezoneDropdown ? timezoneSearch : getSelectedTimezoneLabel()}
                    onChange={(e) => {
                      setTimezoneSearch(e.target.value);
                      setShowTimezoneDropdown(true);
                    }}
                    onFocus={() => {
                      setTimezoneSearch("");
                      setShowTimezoneDropdown(true);
                    }}
                    placeholder="Search for a timezone..."
                    size="lg"
                    borderWidth="2px"
                  />
                  {showTimezoneDropdown && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="lg"
                      maxH="300px"
                      overflowY="auto"
                      zIndex={1000}
                    >
                      {filteredTimezones.length > 0 ? (
                        filteredTimezones.map((tz, index) => (
                          <Box
                            key={`${tz.name}-${index}`}
                            px={4}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: "blue.50" }}
                            bg={tz.name === selectedTimezone ? "blue.100" : "white"}
                            onClick={() => {
                              setSelectedTimezone(tz.name);
                              setShowTimezoneDropdown(false);
                              setTimezoneSearch("");
                            }}
                          >
                            <Text fontSize="sm">{tz.label}</Text>
                          </Box>
                        ))
                      ) : (
                        <Box px={4} py={2}>
                          <Text fontSize="sm" color="gray.500">
                            No timezones found
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </FormControl>
                <Button
                  colorScheme="blue"
                  size="lg"
                  onClick={handleTimezoneChange}
                  isLoading={saving}
                  isDisabled={selectedTimezone === currentTimezone}
                >
                  Save
                </Button>
              </HStack>
            </Box>

            {/* Workers Availability Section */}
            <Box mt={8}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="sm">
                  Workers
                </Heading>
                {workers.length > 0 && (
                  <Input
                    placeholder="Search workers..."
                    value={workerSearch}
                    onChange={(e) => setWorkerSearch(e.target.value)}
                    maxW="300px"
                    size="sm"
                  />
                )}
              </Flex>

              {workers.length === 0 ? (
                <Alert status="info" borderRadius="lg">
                  <AlertIcon />
                  <AlertDescription>
                    No workers found. Please sync with Jobber first.
                  </AlertDescription>
                </Alert>
              ) : filteredWorkers.length === 0 ? (
                <Box p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" textAlign="center">
                  <Text color="gray.500">No workers found matching "{workerSearch}"</Text>
                </Box>
              ) : (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                  {filteredWorkers.map((worker) => (
                    <Box
                      key={worker.id}
                      p={{ base: 4, md: 5 }}
                      borderRadius="xl"
                      bg="white"
                      border="2px solid"
                      borderColor={worker.available ? "green.200" : "gray.200"}
                      opacity={worker.available ? 1 : 0.6}
                    >
                      {/* Worker Header */}
                      <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} direction={{ base: "column", sm: "row" }} gap={3} mb={4}>
                        <Heading size="sm">{worker.name}</Heading>
                        <HStack>
                          <Text fontSize="sm" fontWeight="medium">
                            {worker.available ? "Available" : "Unavailable"}
                          </Text>
                          <Switch
                            colorScheme="green"
                            size="lg"
                            isChecked={worker.available}
                            onChange={() => handleToggleAvailable(worker.id, worker.available)}
                          />
                        </HStack>
                      </Flex>

                      {worker.available && (
                        <>
                          {/* Weekly Schedule Grid */}
                          <Box mb={4}>
                            <SimpleGrid columns={{ base: 1, sm: 2, md: 7 }} spacing={2}>
                              {DAYS.map((day, dayIndex) => {
                                const slots = workersAvailability[worker.id]?.[dayIndex] || [];
                                const isOff = slots.length === 0;

                                return (
                                  <VStack key={dayIndex} spacing={1} align={{ base: "stretch", md: "center" }}>
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                                      {day.slice(0, 3)}
                                    </Text>
                                    <Box
                                      p={2}
                                      bg={isOff ? "gray.100" : "green.50"}
                                      borderRadius="md"
                                      fontSize="xs"
                                      textAlign="center"
                                      minH="40px"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      <Text fontSize="xs" fontWeight="medium">
                                        {formatTimeSlots(slots)}
                                      </Text>
                                    </Box>
                                  </VStack>
                                );
                              })}
                            </SimpleGrid>
                          </Box>

                          {/* Edit Schedule Button */}
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            width="full"
                            leftIcon={<EditIcon />}
                            onClick={() => setExpandedWorker(expandedWorker === worker.id ? null : worker.id)}
                          >
                            {expandedWorker === worker.id ? "Hide Schedule" : "Edit Schedule"}
                          </Button>

                          {/* Expanded Schedule Editor */}
                          {expandedWorker === worker.id && (
                            <Box mt={4} p={4} bg="gray.50" borderRadius="lg">
                              <VStack spacing={4} align="stretch">
                                {DAYS.map((day, dayIndex) => {
                                  const slots = workersAvailability[worker.id]?.[dayIndex] || [];

                                  return (
                                    <Box key={dayIndex}>
                                      <Flex justify="space-between" align="center" mb={2}>
                                        <Text fontWeight="semibold" fontSize="sm">{day}</Text>
                                        <IconButton
                                          icon={<AddIcon />}
                                          size="xs"
                                          colorScheme="blue"
                                          onClick={() => {
                                            setEditingAvailability({ workerId: worker.id, day: dayIndex });
                                            setNewSlot({ day: dayIndex, startTime: '09:00', endTime: '17:00' });
                                          }}
                                        />
                                      </Flex>

                                      {/* Existing Slots */}
                                      {slots.map((slot) => (
                                        <Box key={slot.id} mb={1}>
                                          {editingSlot === slot.id ? (
                                            <Box p={3} bg="blue.50" borderRadius="md">
                                              <HStack spacing={2} mb={2}>
                                                <Input
                                                  type="time"
                                                  size="sm"
                                                  value={editSlotData.startTime}
                                                  onChange={(e) => setEditSlotData({ ...editSlotData, startTime: e.target.value })}
                                                />
                                                <Text fontSize="sm">to</Text>
                                                <Input
                                                  type="time"
                                                  size="sm"
                                                  value={editSlotData.endTime}
                                                  onChange={(e) => setEditSlotData({ ...editSlotData, endTime: e.target.value })}
                                                />
                                              </HStack>
                                              <HStack spacing={2}>
                                                <IconButton
                                                  icon={<CheckIcon />}
                                                  size="sm"
                                                  colorScheme="green"
                                                  onClick={() => handleUpdateTimeSlot(worker.id, slot.id)}
                                                  isLoading={saving}
                                                />
                                                <IconButton
                                                  icon={<CloseIcon />}
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() => {
                                                    setEditingSlot(null);
                                                    setEditSlotData({ startTime: '', endTime: '' });
                                                  }}
                                                />
                                              </HStack>
                                            </Box>
                                          ) : (
                                            <Flex justify="space-between" align="center" p={2} bg="white" borderRadius="md">
                                              <Text fontSize="sm">
                                                {convertTo12Hour(slot.start_time)} - {convertTo12Hour(slot.end_time)}
                                              </Text>
                                              <HStack spacing={1}>
                                                <IconButton
                                                  icon={<EditIcon />}
                                                  size="xs"
                                                  colorScheme="blue"
                                                  variant="ghost"
                                                  onClick={() => {
                                                    setEditingSlot(slot.id);
                                                    setEditSlotData({
                                                      startTime: slot.start_time,
                                                      endTime: slot.end_time
                                                    });
                                                  }}
                                                />
                                                <IconButton
                                                  icon={<DeleteIcon />}
                                                  size="xs"
                                                  colorScheme="red"
                                                  variant="ghost"
                                                  onClick={() => handleDeleteTimeSlot(worker.id, slot.id)}
                                                />
                                              </HStack>
                                            </Flex>
                                          )}
                                        </Box>
                                      ))}

                                      {/* Add Slot Form */}
                                      {editingAvailability?.workerId === worker.id && editingAvailability?.day === dayIndex && (
                                        <Box p={3} bg="blue.50" borderRadius="md" mt={2}>
                                          <HStack spacing={2} mb={2}>
                                            <Input
                                              type="time"
                                              size="sm"
                                              value={newSlot.startTime}
                                              onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                                            />
                                            <Text fontSize="sm">to</Text>
                                            <Input
                                              type="time"
                                              size="sm"
                                              value={newSlot.endTime}
                                              onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                                            />
                                          </HStack>
                                          <HStack spacing={2}>
                                            <IconButton
                                              icon={<CheckIcon />}
                                              size="sm"
                                              colorScheme="green"
                                              onClick={() => handleAddTimeSlot(worker.id, dayIndex)}
                                              isLoading={saving}
                                            />
                                            <IconButton
                                              icon={<CloseIcon />}
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => setEditingAvailability(null)}
                                            />
                                          </HStack>
                                        </Box>
                                      )}

                                      {slots.length === 0 && (!editingAvailability || editingAvailability.workerId !== worker.id || editingAvailability.day !== dayIndex) && (
                                        <Text fontSize="xs" color="gray.500">No hours set</Text>
                                      )}
                                    </Box>
                                  );
                                })}
                              </VStack>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Box>

            {/* Days Off Section */}
            <Box mt={8}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="sm">Days Off & Holidays</Heading>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<AddIcon />}
                  onClick={() => setShowAddDayOffModal(true)}
                >
                  Add Day Off
                </Button>
              </Flex>

              {allDaysOff.length === 0 ? (
                <Box p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" textAlign="center">
                  <Text color="gray.500">No days off scheduled</Text>
                </Box>
              ) : (
                <VStack spacing={2} align="stretch">
                  {allDaysOff.slice(0, 5).map((dayOff) => (
                    <Flex
                      key={dayOff.id}
                      p={4}
                      bg="white"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="gray.200"
                      justify="space-between"
                      align="center"
                    >
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold">{dayOff.name}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {dayOff.worker_name} • {dayOff.date_range_display}
                        </Text>
                      </VStack>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleDeleteDayOff(dayOff.id)}
                      />
                    </Flex>
                  ))}
                </VStack>
              )}
            </Box>

            {/* Navigation Buttons */}
            <HStack mt={8} justify="space-between">
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate("/onboarding/waitlist/customize")}
              >
                Back
              </Button>
              <Button
                colorScheme="blue"
                size="lg"
                onClick={handleContinue}
              >
                Continue to Turn It On
              </Button>
            </HStack>

      {/* Add Day Off Modal */}
      {showAddDayOffModal && (
        <AddDayOffModalChakra
          workers={workers.map(w => ({ id: w.id, name: w.name }))}
          categories={CATEGORIES}
          dayOff={null}
          onClose={() => setShowAddDayOffModal(false)}
          onSubmit={handleAddDayOff}
        />
      )}
    </OnboardingLayout>
  );
}
